// MIDAS V3 — Step pill (one visual family for every step state)
//
// MIDAS-INFLIGHT-UNIFY (2026-11-03, Creative Designer): owner ruling "why are
// there 2 different styles? it should be unified." Every step the user sees —
// the in-flight status, a loop_progress step that just settled, and the marker
// rows that persist in the streamed prose / final answer — now renders as the
// SAME pill silhouette the phase-1 streaming chrome introduced (rounded pill,
// 6 px dot slot, mono uppercase 10.5 / 0.06em label). Two states:
//   • live (`live` prop) — accent-soft fill, accent-line border, accent text,
//     pulsing accent dot + left→right shimmer sweep (the phase-1 look).
//   • done (default)     — quiet neutral pill: no fill, --border hairline,
//     --muted text, a small --green check in the dot slot. Same geometry, so a
//     live step visibly "settles" into its done twin without a layout jump.
//
// No ellipsis is ever APPENDED here. Bot `live_label`s and the i18n
// `midas.v3.streaming.steps.*` labels already end in "…"; the old plain row
// appended its own "…" glyph, which produced the doubled "… …". Liveness is
// carried by the pulsing dot + shimmer instead.
//
// Long labels wrap inside the pill (radius 14 ≥ half a single-line height, so a
// one-line pill is still fully round) rather than clipping on a phone column.
//
// Label is plain text (bot-composed, already in thread language — never
// translated FE-side, never markdown/chip-processed).
//
// Reduced-motion: dot pulse + shimmer use the shared `midas-pulse` /
// `midas-shimmer` keyframes, defined inside the no-preference block of
// midas-v3-tokens.scss and neutralised by its global reduce override.
//
// No MUI, no @emotion, no `sx`, no `dt` tokens.
import React from "react";

export interface StepRowV3Props {
  /** Plain-text step label. */
  label: string;
  /** Live (in-progress) variant — accent pill with pulsing dot + shimmer. */
  live?: boolean;
  /** Vertical rhythm for rows that sit between prose blocks (default). Pass
   *  false when a parent layout owns the spacing (the in-flight status row). */
  inFlow?: boolean;
}

const CheckGlyph: React.FC = () => (
  <svg width="9" height="9" viewBox="0 0 10 10" aria-hidden="true" focusable="false">
    <path
      d="M1.6 5.3 4 7.6 8.4 2.6"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const StepRowV3: React.FC<StepRowV3Props> = ({ label, live = false, inFlow = true }) => {
  return (
    <div
      className="midas-v3-step-row"
      data-live={live ? "true" : undefined}
      style={{
        display: "flex",
        minWidth: 0,
        // Heading-like rhythm between prose blocks: clear gap above, snug below.
        margin: inFlow ? "14px 0 6px" : 0,
      }}
    >
      <span
        style={{
          position: "relative",
          display: "inline-flex",
          alignItems: "center",
          gap: 8,
          maxWidth: "100%",
          minWidth: 0,
          boxSizing: "border-box",
          padding: "5px 12px 5px 10px",
          borderRadius: 14,
          background: live ? "var(--accent-soft)" : "transparent",
          border: `1px solid ${live ? "var(--accent-line)" : "var(--border)"}`,
          color: live ? "var(--accent)" : "var(--muted)",
          overflow: "hidden",
        }}
      >
        {/* Dot slot — 9 px wide in both states so the label never shifts. */}
        <span
          aria-hidden="true"
          style={{
            width: 9,
            flexShrink: 0,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            color: "var(--green)",
          }}
        >
          {live ? (
            <span
              style={{
                width: 6,
                height: 6,
                borderRadius: 999,
                background: "var(--accent)",
                animation: "midas-pulse 1.2s ease-in-out infinite",
              }}
            />
          ) : (
            <CheckGlyph />
          )}
        </span>
        <span
          className="mono"
          style={{
            position: "relative",
            zIndex: 1,
            minWidth: 0,
            fontSize: 10.5,
            lineHeight: 1.45,
            letterSpacing: "0.06em",
            textTransform: "uppercase",
            wordBreak: "break-word",
          }}
        >
          {label}
        </span>
        {live && (
          <span
            aria-hidden="true"
            style={{
              position: "absolute",
              inset: 0,
              background:
                "linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.35) 50%, transparent 100%)",
              transform: "translateX(-100%)",
              animation: "midas-shimmer 2s linear infinite",
              pointerEvents: "none",
            }}
          />
        )}
      </span>
    </div>
  );
};

export default StepRowV3;
